<template>
  <v-card class="location-reset-dialog d-flex flex-column">
    <CardToolbar title="Location Data Reset" :isModal="true" @cancel="close" />

    <v-card-text class="location-reset-dialog__body pt-5">
      <v-alert v-if="errorMessage" type="error" outlined dense>
        {{ errorMessage }}
      </v-alert>

      <template v-if="step === 1">
        <h3 class="text-h6 mb-2">How would you like to start over?</h3>
        <p class="grey--text text--darken-1">
          This only affects <strong>{{ locationName }}</strong>. Nothing is
          removed until you review the preview and confirm your identity.
        </p>
        <v-radio-group v-model="preset" class="mt-1">
          <v-card outlined class="mb-3 pa-3" @click="preset = 'factory'">
            <v-radio value="factory" label="Factory reset this location" />
            <div class="pl-8 text-body-2 grey--text text--darken-1">
              Remove all operational data defined by the server while keeping
              the location and its protected configuration.
            </div>
          </v-card>
          <v-card outlined class="pa-3" @click="preset = 'custom'">
            <v-radio value="custom" label="Choose what to remove" />
            <div class="pl-8 text-body-2 grey--text text--darken-1">
              Select individual data groups and review their dependencies.
            </div>
          </v-card>
        </v-radio-group>
      </template>

      <template v-else-if="step === 2">
        <div class="d-flex align-center mb-2">
          <h3 class="text-h6">Review data removal</h3>
          <v-spacer />
          <v-chip small outlined>{{ presetLabel }}</v-chip>
        </div>
        <p class="text-body-2">
          Counts are a preview. Related records required by a selected group
          are included automatically.
        </p>

        <v-list outlined class="rounded mb-4">
          <v-list-item v-for="group in normalizedGroups" :key="group.id">
            <v-list-item-action v-if="preset === 'custom'">
              <v-checkbox
                :input-value="isSelected(group.id)"
                :disabled="group.blocked || isDependencyLocked(group.id)"
                color="error"
                @change="toggleGroup(group.id, $event)"
              />
            </v-list-item-action>
            <v-list-item-icon v-else>
              <v-icon :color="group.blocked ? 'grey' : 'error'">
                {{ group.blocked ? "mdi-lock-outline" : "mdi-delete-outline" }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ group.label }}</v-list-item-title>
              <v-list-item-subtitle v-if="group.description">
                {{ group.description }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="group.blocked" class="error--text">
                {{ group.blockedReason || "This data cannot be removed." }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip small>{{ formatCount(group.count) }}</v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-alert type="success" text dense>
          <div class="font-weight-medium mb-1">Data that will be retained</div>
          <span v-if="retainedLabels.length">{{ retainedLabels.join(", ") }}</span>
          <span v-else>The server did not report any retained groups.</span>
        </v-alert>
      </template>

      <template v-else-if="step === 3">
        <h3 class="text-h6 mb-2">Verify your identity</h3>
        <p>Enter the password for <strong>{{ email }}</strong> to continue.</p>
        <v-text-field
          v-model="password"
          label="Password"
          outlined
          dense
          :append-icon="showPassword ? 'visibility_off' : 'visibility'"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="current-password"
          @click:append="showPassword = !showPassword"
          @keyup.enter.prevent="verifyPassword"
        />
      </template>

      <template v-else-if="step === 4">
        <v-alert type="warning" outlined>
          This deletion is permanent. Type
          <strong>{{ confirmationToken }}</strong> to confirm.
        </v-alert>
        <v-text-field
          v-model="confirmation"
          :label="`Type ${confirmationToken} to confirm`"
          outlined
          dense
          autocomplete="off"
          @keyup.enter.prevent="executeReset"
        />
      </template>

      <template v-else>
        <v-alert type="success" outlined>
          Location data reset completed successfully.
        </v-alert>
        <v-simple-table dense>
          <tbody>
            <tr>
              <th>Receipt</th>
              <td>{{ receiptId }}</td>
            </tr>
            <tr v-if="result.completedAt">
              <th>Completed</th>
              <td>{{ result.completedAt | moment("LLLL") }}</td>
            </tr>
            <tr>
              <th>Location</th>
              <td>{{ locationName }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <div class="mt-4">
          <strong>Removed:</strong> {{ resultDeletedSummary }}
        </div>
      </template>
    </v-card-text>

    <v-divider />
    <v-card-actions class="pa-3">
      <v-btn v-if="step > 1 && step < 5" text :disabled="loading" @click="step--">
        Back
      </v-btn>
      <v-spacer />
      <v-btn text :disabled="loading" @click="close">
        {{ step === 5 ? "Close" : "Cancel" }}
      </v-btn>
      <v-btn
        v-if="step === 1"
        color="primary"
        :loading="loading"
        @click="loadPreview"
      >
        Preview
      </v-btn>
      <v-btn
        v-else-if="step === 2"
        color="primary"
        :loading="loading"
        :disabled="selectedGroups.length === 0"
        @click="continueFromReview"
      >
        Continue
      </v-btn>
      <v-btn
        v-else-if="step === 3"
        color="primary"
        :loading="loading"
        :disabled="!password"
        @click="verifyPassword"
      >
        Verify
      </v-btn>
      <v-btn
        v-else-if="step === 4"
        color="error"
        :loading="loading"
        :disabled="!isConfirmationValid"
        @click="executeReset"
      >
        Permanently Reset
      </v-btn>
      <v-btn v-else color="primary" outlined @click="copyReceipt">
        <v-icon left small>mdi-content-copy</v-icon>
        Copy Receipt
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import authService from "@/modules/Authentication/service";
import locationService from "../service";

export default {
  name: "LocationResetDialog",
  props: {
    LocationId: { type: Number, required: true },
    location: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      step: 1,
      preset: "factory",
      preview: {},
      selectedGroups: [],
      password: "",
      showPassword: false,
      confirmation: "",
      result: {},
      loading: false,
      errorMessage: "",
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    email() {
      return this.currentUser?.email || "your account";
    },
    locationName() {
      return this.preview?.location?.name || this.location?.name || "this location";
    },
    presetLabel() {
      return this.preset === "factory" ? "Factory reset" : "Custom reset";
    },
    confirmationSentence() {
      return this.preview.confirmationSentence || `RESET ${this.locationName} LOCATION DATA`;
    },
    confirmationToken() {
      return String(this.locationName || "location")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();
    },
    isConfirmationValid() {
      return String(this.confirmation || "").trim().toLowerCase() === this.confirmationToken;
    },
    normalizedGroups() {
      const selectedSource = this.preview.groups || [];
      const catalog = Array.isArray(this.preview.catalog)
        ? this.preview.catalog
        : [];
      const groupSource = catalog.length ? catalog : selectedSource;
      const groups = Array.isArray(groupSource)
        ? groupSource
        : Object.keys(groupSource).length
        ? Object.keys(groupSource).map(id => ({ id, ...groupSource[id] }))
        : Object.keys(this.preview.counts || {});
      return groups.map(group => {
        const raw = typeof group === "string" ? { id: group } : group;
        const id = raw.id || raw.key || raw.name;
        const dependencies = this.dependenciesFor(id);
        const blockedMetadata = this.blockedFor(id);
        return {
          ...raw,
          id,
          label: raw.label || this.humanize(id),
          count: raw.count ?? (this.preview.counts || {})[id] ?? 0,
          dependsOn: raw.dependsOn || raw.dependencies || dependencies,
          blocked: Boolean(raw.blocked || raw.disabled || blockedMetadata.blocked),
          blockedReason: raw.blockedReason || raw.reason || blockedMetadata.reason,
        };
      });
    },
    retainedLabels() {
      const retained = this.preview.retained || [];
      return (Array.isArray(retained) ? retained : Object.keys(retained)).map(item =>
        typeof item === "string" ? this.humanize(item) : item.label || this.humanize(item.id)
      );
    },
    receiptId() {
      return this.result.operationId || this.result.receiptId || "Not provided";
    },
    resultDeletedSummary() {
      const deleted = this.result.deleted || this.result.counts || {};
      if (Array.isArray(deleted)) {
        return deleted.map(item => `${item.label || this.humanize(item.id)} (${this.formatCount(item.count)})`).join(", ");
      }
      const summary = Object.keys(deleted).map(id => `${this.humanize(id)} (${this.formatCount(deleted[id])})`);
      return summary.join(", ") || "Completed according to the server receipt.";
    },
  },
  methods: {
    close() {
      if (!this.loading) this.$emit("close");
    },
    humanize(value) {
      return String(value || "Data")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[_-]/g, " ")
        .replace(/^./, letter => letter.toUpperCase());
    },
    formatCount(value) {
      return this.countTotal(value).toLocaleString();
    },
    countTotal(value) {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }
      if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      if (Array.isArray(value)) {
        return value.reduce((total, item) => total + this.countTotal(item), 0);
      }
      if (value && typeof value === "object") {
        return Object.keys(value).reduce(
          (total, key) => total + this.countTotal(value[key]),
          0
        );
      }
      return 0;
    },
    getError(error) {
      return error?.response?.data?.message || error?.data?.message || error?.message || "Unable to complete the request.";
    },
    dependenciesFor(id) {
      const metadata = this.preview.dependencies || {};
      if (Array.isArray(metadata)) {
        const match = metadata.find(item =>
          item && (item.id === id || item.group === id || item.source === id)
        );
        return match?.dependsOn || match?.requires || [];
      }
      return metadata[id]?.dependsOn || metadata[id]?.requires || metadata[id] || [];
    },
    blockedFor(id) {
      const metadata = this.preview.blocked || {};
      if (Array.isArray(metadata)) {
        const match = metadata.find(item =>
          item === id || (item && (item.id === id || item.group === id))
        );
        return typeof match === "string"
          ? { blocked: true }
          : { blocked: Boolean(match), reason: match?.reason || match?.message };
      }
      const value = metadata[id];
      return typeof value === "string"
        ? { blocked: true, reason: value }
        : { blocked: Boolean(value), reason: value?.reason || value?.message };
    },
    groupIds(groups) {
      if (Array.isArray(groups)) {
        return groups
          .map(group =>
            typeof group === "string"
              ? group
              : group?.id || group?.key || group?.name
          )
          .filter(Boolean);
      }
      return groups && typeof groups === "object" ? Object.keys(groups) : [];
    },
    isSelected(id) {
      return this.selectedGroups.includes(id);
    },
    isDependencyLocked(id) {
      return this.normalizedGroups.some(group =>
        this.isSelected(group.id) && (group.dependsOn || []).includes(id)
      );
    },
    selectWithDependencies(id) {
      if (!id || this.selectedGroups.includes(id)) return;
      this.selectedGroups.push(id);
      const group = this.normalizedGroups.find(item => item.id === id);
      (group?.dependsOn || []).forEach(this.selectWithDependencies);
    },
    toggleGroup(id, checked) {
      if (checked) this.selectWithDependencies(id);
      else if (!this.isDependencyLocked(id)) {
        this.selectedGroups = this.selectedGroups.filter(item => item !== id);
      }
    },
    async loadPreview() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await locationService.Reset.preview(this.LocationId, {
          preset: this.preset,
          groups: this.preset === "custom" ? this.selectedGroups : undefined,
        });
        this.preview = response.data || response;
        this.selectedGroups = [];
        const serverSelected = this.groupIds(this.preview.groups);
        const hasCatalog =
          Array.isArray(this.preview.catalog) && this.preview.catalog.length > 0;
        this.normalizedGroups
          .filter(
            group =>
              !group.blocked &&
              (serverSelected.includes(group.id) ||
                (!hasCatalog && group.selectedByDefault))
          )
          .forEach(group => this.selectWithDependencies(group.id));
        this.step = 2;
      } catch (error) {
        this.errorMessage = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    async continueFromReview() {
      if (this.preset !== "custom") {
        this.step = 3;
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      const requestedGroups = [...this.selectedGroups];
      try {
        const response = await locationService.Reset.preview(this.LocationId, {
          preset: "custom",
          groups: requestedGroups,
        });
        this.preview = response.data || response;
        const resolvedGroups = this.groupIds(this.preview.groups);
        this.selectedGroups = [];
        (resolvedGroups.length ? resolvedGroups : requestedGroups).forEach(
          id => this.selectWithDependencies(id)
        );
        this.step = 3;
      } catch (error) {
        this.errorMessage = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    async verifyPassword() {
      this.loading = true;
      this.errorMessage = "";
      try {
        await authService.verify({ email: this.currentUser.email, password: this.password });
        this.password = "";
        this.step = 4;
      } catch (error) {
        this.errorMessage = this.getError(error) || "Invalid password.";
      } finally {
        this.loading = false;
      }
    },
    async executeReset() {
      if (!this.isConfirmationValid) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await locationService.Reset.execute(this.LocationId, {
          preset: this.preset,
          groups: this.selectedGroups,
          previewToken: this.preview.previewToken,
          confirmation: this.confirmationSentence,
        });
        this.result = response.data || response;
        this.step = 5;
      } catch (error) {
        this.errorMessage = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    async copyReceipt() {
      const text = `Location reset receipt: ${this.receiptId}\nLocation: ${this.locationName}\nRemoved: ${this.resultDeletedSummary}`;
      try {
        await navigator.clipboard.writeText(text);
        this.$toasted.success("Receipt copied", { position: "top-right", duration: 2000 });
      } catch (error) {
        this.$toasted.error("Unable to copy receipt", { position: "top-right", duration: 2000 });
      }
    },
  },
};
</script>

<style scoped>
.location-reset-dialog {
  max-height: calc(100vh - 48px);
  overflow: hidden;
}

.location-reset-dialog__body {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
